import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';

export default function RouteFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [items, setItems] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    route_name: '', item_id: '', revision: '0', description: '',
    bill_of_materials: [],
    output_items: [],  // NEW: Output items (FG + Scrap)
    operations: [],
  });

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [itemsRes, wcRes] = await Promise.all([
        inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' }),
        inventoryAPI.listWorkCenters(),
      ]);
      setItems(itemsRes.data.items);
      setWorkCenters(wcRes.data);
      if (isEdit) {
        const routeRes = await inventoryAPI.getRoute(id);
        const r = routeRes.data;
        setForm({
          route_name: r.route_name, item_id: r.item_id,
          revision: r.revision || '0', description: r.description || '',
          bill_of_materials: r.bill_of_materials || [],
          output_items: r.output_items || [],
          operations: r.operations || [],
        });
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  const addBOMItem = () => {
    setForm(f => ({ ...f, bill_of_materials: [...f.bill_of_materials, { item_id: '', item_name: '', quantity: 1, unit_of_measure: 'Nos' }] }));
  };

  const removeBOMItem = (idx) => {
    setForm(f => ({ ...f, bill_of_materials: f.bill_of_materials.filter((_, i) => i !== idx) }));
  };

  const updateBOMItem = (idx, field, value) => {
    setForm(f => {
      const bom = [...f.bill_of_materials];
      bom[idx] = { ...bom[idx], [field]: value };
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        bom[idx].item_name = item?.item_name || '';
        bom[idx].unit_of_measure = item?.unit_of_measure || 'Nos';
      }
      return { ...f, bill_of_materials: bom };
    });
  };

  // Output Items (FG + Scrap)
  const addOutputItem = (outputType) => {
    setForm(f => ({ ...f, output_items: [...f.output_items, { item_id: '', item_name: '', quantity: 1, unit_of_measure: 'Nos', output_type: outputType }] }));
  };

  const removeOutputItem = (idx) => {
    setForm(f => ({ ...f, output_items: f.output_items.filter((_, i) => i !== idx) }));
  };

  const updateOutputItem = (idx, field, value) => {
    setForm(f => {
      const outputs = [...f.output_items];
      outputs[idx] = { ...outputs[idx], [field]: value };
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        outputs[idx].item_name = item?.item_name || '';
        outputs[idx].unit_of_measure = item?.unit_of_measure || 'Nos';
      }
      return { ...f, output_items: outputs };
    });
  };

  const addOperation = () => {
    const nextNum = (form.operations.length + 1) * 10;
    setForm(f => ({
      ...f, operations: [...f.operations, {
        operation_number: nextNum, operation_description: '', work_center_id: '', work_center_name: '',
        setup_time_minutes: 0, cycle_time_minutes: 0, is_subcontract: false,
        subcontract_supplier_id: '', subcontract_supplier_name: '',
        is_quality_checkpoint: false, quality_parameters: '', work_instructions: '',
      }]
    }));
  };

  const removeOperation = (idx) => {
    setForm(f => ({ ...f, operations: f.operations.filter((_, i) => i !== idx) }));
  };

  const updateOperation = (idx, field, value) => {
    setForm(f => {
      const ops = [...f.operations];
      ops[idx] = { ...ops[idx], [field]: value };
      if (field === 'work_center_id') {
        const wc = workCenters.find(w => w.id === value);
        ops[idx].work_center_name = wc?.name || '';
      }
      return { ...f, operations: ops };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Derive item_id from first FG output item
    const firstFG = form.output_items.find(o => o.output_type === 'finished_good');
    if (!form.route_name) { toast.error('Route name is required'); return; }
    if (!firstFG?.item_id) { toast.error('At least one Finished Good output item is required'); return; }
    const submitForm = { ...form, item_id: firstFG.item_id };
    setLoading(true);
    try {
      if (isEdit) {
        await inventoryAPI.updateRoute(id, submitForm);
        toast.success('Route updated');
      } else {
        await inventoryAPI.createRoute(submitForm);
        toast.success('Route created');
      }
      navigate('/production/routes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save route');
    } finally { setLoading(false); }
  };

  // Filter items by type for BOM material selection
  const BOM_ELIGIBLE_TYPES = ['Raw Material', 'Service', 'Consumable', 'Semi-Finished Part', 'Finished Good', 'Scrap', 'Job Work Item'];
  const bomEligibleItems = items.filter(i => BOM_ELIGIBLE_TYPES.includes(i.item_type));
  const makeItems = items.filter(i => i.make_or_buy?.toLowerCase() === 'make' || i.item_type === 'Finished Good');
  const scrapEligibleItems = items.filter(i => i.make_or_buy?.toLowerCase() === 'make' || i.item_type === 'Scrap' || i.item_type === 'Finished Good' || i.category?.toLowerCase() === 'scrap');

  return (
    <div className="space-y-4 max-w-4xl" data-testid="route-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="font-heading text-xl font-bold tracking-tight">{isEdit ? 'Edit Route' : 'New Route'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Route Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Route Name *</Label>
              <Input value={form.route_name} onChange={e => setForm(f => ({...f, route_name: e.target.value}))}
                placeholder="e.g. Clamp Machining Route" className="h-8 text-xs" data-testid="route-name" />
            </div>
            <div>
              <Label className="text-xs">Primary Output</Label>
              <p className="text-xs text-muted-foreground mt-1 p-2 bg-muted/50 rounded h-8 flex items-center">
                {(() => {
                  const fg = form.output_items.find(o => o.output_type === 'finished_good');
                  if (fg?.item_id) {
                    const item = items.find(i => i.id === fg.item_id);
                    return item ? `${item.unique_id} - ${item.item_name}` : 'Selected below';
                  }
                  return 'Add a Finished Good in Output Items below';
                })()}
              </p>
            </div>
            <div>
              <Label className="text-xs">Revision</Label>
              <Input value={form.revision} onChange={e => setForm(f => ({...f, revision: e.target.value}))}
                className="h-8 text-xs" data-testid="route-revision" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                className="text-xs min-h-[50px]" data-testid="route-description" />
            </div>
          </CardContent>
        </Card>

        {/* Bill of Materials */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Bill of Materials</CardTitle>
              <Button type="button" size="sm" variant="outline" onClick={addBOMItem} data-testid="add-bom-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Material
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {form.bill_of_materials.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No materials added</p>
            ) : (
              <div className="space-y-2">
                {form.bill_of_materials.map((bom, idx) => (
                  <div key={idx} className="flex gap-2 items-end p-2 rounded border bg-muted/30" data-testid={`bom-row-${idx}`}>
                    <div className="flex-1">
                      <Label className="text-[10px]">Material</Label>
                      <SearchableSelect
                        options={bomEligibleItems.map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: i.item_type, searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''}` }))}
                        value={bom.item_id}
                        onSelect={v => updateBOMItem(idx, 'item_id', v)}
                        placeholder="Search materials..."
                        emptyText="No items."
                        triggerClassName="h-7"
                      />
                    </div>
                    <div className="w-24">
                      <Label className="text-[10px]">Qty</Label>
                      <Input type="number" step="0.01" value={bom.quantity}
                        onChange={e => updateBOMItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs" />
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px]">UOM</Label>
                      <Input value={bom.unit_of_measure} disabled className="h-7 text-xs bg-muted" />
                    </div>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeBOMItem(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Output Items (FG + Scrap) */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-medium">Output Items</CardTitle>
                <p className="text-[10px] text-muted-foreground mt-0.5">Define what this route produces: Finished Goods + Scrap</p>
              </div>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="outline" onClick={() => addOutputItem('finished_good')} data-testid="add-fg-btn">
                  <Plus className="w-3 h-3 mr-1" /> Finished Good
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => addOutputItem('scrap')} data-testid="add-scrap-btn">
                  <Plus className="w-3 h-3 mr-1" /> Scrap
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {form.output_items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No output items defined. Add finished goods and/or scrap produced by this route.</p>
            ) : (
              <div className="space-y-2">
                {form.output_items.map((out, idx) => (
                  <div key={idx} className={`flex items-end gap-2 p-2 rounded border ${out.output_type === 'scrap' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`} data-testid={`output-row-${idx}`}>
                    <div className="w-14">
                      <Badge variant={out.output_type === 'scrap' ? 'secondary' : 'default'} className="text-[9px]">
                        {out.output_type === 'scrap' ? 'SCRAP' : 'FG'}
                      </Badge>
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px]">Item</Label>
                      <SearchableSelect
                        options={(out.output_type === 'scrap' ? scrapEligibleItems : makeItems).map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: i.item_type, searchStr: `${i.unique_id} ${i.item_name}` }))}
                        value={out.item_id}
                        onSelect={v => updateOutputItem(idx, 'item_id', v)}
                        placeholder={out.output_type === 'scrap' ? "Search scrap/make items..." : "Search make items..."}
                        emptyText="No items."
                        testId={`output-item-${idx}`}
                      />
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px]">Qty</Label>
                      <Input type="number" step="0.01" value={out.quantity}
                        onChange={e => updateOutputItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs" />
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px]">UOM</Label>
                      <Input value={out.unit_of_measure} disabled className="h-7 text-xs bg-muted" />
                    </div>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeOutputItem(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Operations */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Operations</CardTitle>
              <Button type="button" size="sm" variant="outline" onClick={addOperation} data-testid="add-operation-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Operation
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {form.operations.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No operations added</p>
            ) : (
              <div className="space-y-3">
                {form.operations.map((op, idx) => (
                  <div key={idx} className="p-3 rounded border bg-muted/30 space-y-2" data-testid={`op-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">Op {op.operation_number}</span>
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeOperation(idx)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px]">Op Number</Label>
                        <Input type="number" value={op.operation_number} onChange={e => updateOperation(idx, 'operation_number', parseInt(e.target.value) || 0)}
                          className="h-7 text-xs" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[10px]">Description</Label>
                        <Input value={op.operation_description} onChange={e => updateOperation(idx, 'operation_description', e.target.value)}
                          placeholder="Operation description" className="h-7 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Work Center</Label>
                        <Select value={op.work_center_id || 'none'} onValueChange={v => updateOperation(idx, 'work_center_id', v === 'none' ? '' : v)}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {workCenters.map(wc => <SelectItem key={wc.id} value={wc.id}>{wc.code} - {wc.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px]">Setup Time (min)</Label>
                        <Input type="number" value={op.setup_time_minutes} onChange={e => updateOperation(idx, 'setup_time_minutes', parseFloat(e.target.value) || 0)}
                          className="h-7 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Cycle Time (min)</Label>
                        <Input type="number" value={op.cycle_time_minutes} onChange={e => updateOperation(idx, 'cycle_time_minutes', parseFloat(e.target.value) || 0)}
                          className="h-7 text-xs" />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <Switch checked={op.is_subcontract} onCheckedChange={v => updateOperation(idx, 'is_subcontract', v)} />
                        <Label className="text-[10px]">Subcontract</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={op.is_quality_checkpoint} onCheckedChange={v => updateOperation(idx, 'is_quality_checkpoint', v)} />
                        <Label className="text-[10px]">Quality Checkpoint</Label>
                      </div>
                    </div>
                    {op.is_quality_checkpoint && (
                      <div>
                        <Label className="text-[10px]">Quality Parameters</Label>
                        <Input value={op.quality_parameters} onChange={e => updateOperation(idx, 'quality_parameters', e.target.value)}
                          placeholder="Parameters to check" className="h-7 text-xs" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-route-btn">
            <Save className="w-4 h-4 mr-1" /> {loading ? 'Saving...' : isEdit ? 'Update Route' : 'Create Route'}
          </Button>
        </div>
      </form>
    </div>
  );
}
